import React, { useEffect, useState } from "react";
import { WithApiAccess } from "../../lib/HigherOrderComponents/WithApiAccess";
import { Spinner } from "react-bootstrap";
import "./styles/NavUserCard.css";

const CACHE_KEY = "cp_nav_user_card";

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Validate shape — guard against stale data from old versions
    if (typeof parsed !== "object" || parsed === null) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(user) {
  if (!user) return;
  try {
    const minimal = {
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      customers: Array.isArray(user.customers)
        ? user.customers.map((c) => ({ name: c?.name || "" }))
        : [],
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(minimal));
  } catch {}
}

function NavUserCardInner({ api, collapsed }) {
  const [user, setUser] = useState(readCache);
  // Start loading only if there's nothing cached — avoids a spinner flash
  // when we already have data to show.
  const [isLoading, setIsLoading] = useState(() => !readCache());

  useEffect(() => {
    let isMounted = true;

    async function refresh() {
      // Have cached details (or no api) → nothing to fetch.
      if (!api || readCache()) {
        if (isMounted) setIsLoading(false);
        return;
      }
      try {
        const res = await api.get("/api/user");
        if (isMounted) {
          setUser(res.data);
          writeCache(res.data);
        }
      } catch (err) {
        console.error("[NavUserCard] Failed to fetch user:", err);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    refresh();

    return () => {
      isMounted = false;
    };
  }, [api]);

  // Loading: only when we're actually fetching and have nothing to show yet.
  if (isLoading && !user) {
    return collapsed ? (
      <div className="cp-user-card cp-user-card--collapsed cp-user-card--loading">
        <Spinner animation="border" size="sm" />
      </div>
    ) : (
      <div className="cp-user-card cp-user-card--loading">
        <Spinner animation="border" size="sm" />
        <span className="cp-user-card__loading-text">Loading…</span>
      </div>
    );
  }

  if (!user) {
    return collapsed ? (
      <div
        className="cp-user-card cp-user-card--collapsed cp-user-card--empty"
        aria-hidden="true"
      />
    ) : (
      <div className="cp-user-card cp-user-card--empty" aria-hidden="true" />
    );
  }

  const initials =
    `${(user.firstName || "")[0] || ""}${(user.lastName || "")[0] || ""}`
      .trim()
      .toUpperCase() || "?";

  const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();

  let customerLabel = null;
  let customerValue = null;
  let customerTooltip = null;

  if (user.customers && user.customers.length > 0) {
    const firstName = user.customers[0]?.name || "Unknown";
    const isMultiple = user.customers.length > 1;
    customerLabel = isMultiple ? "Customers:" : "Customer:";
    customerValue = isMultiple ? `${firstName} and more` : firstName;
    customerTooltip = user.customers.map((c) => c.name || "Unknown").join(", ");
  }

  if (collapsed) {
    return (
      <div className="cp-user-card cp-user-card--collapsed">
        <div
          className="cp-user-card__avatar"
          title={fullName}
          aria-hidden="true"
        >
          {initials}
        </div>
      </div>
    );
  }

  return (
    <div className="cp-user-card">
      <div className="cp-user-card__avatar" aria-hidden="true">
        {initials}
      </div>
      <div className="cp-user-card__meta">
        <div className="cp-user-card__name" title={fullName}>
          {fullName}
        </div>
        {customerValue && (
          <div className="cp-user-card__customer">
            <span className="cp-user-card__customer-label">
              {customerLabel}
            </span>
            <span
              className="cp-user-card__customer-value"
              title={customerTooltip}
            >
              {customerValue}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export const NavUserCard = WithApiAccess(NavUserCardInner);
